/**
 * 选区像素编辑（M2-20 冒烟链路需要的「删除选区内容」）。
 *
 * Photoshop 语义：普通图层上 Delete = 选区内像素变透明。
 * 一次调用 = 一段像素事务 = 一条历史（与画笔/填充同一纪律，M1-08）。
 * 选区值按比例调制：128 处删一半（与填充的选区调制对偶）。
 */
import { TILE, tileKey, type LayerId } from '../engine/tile/key';
import { BYTES_PER_TILE } from '../engine/tile/store';
import { applyFill } from '../engine/fill/apply';
import { contentAwareFill, type ContentAwareFillOptions } from '../engine/fill/content-aware';
import type { SeparableBlendMode } from '../gpu/shaders/blend/modes';
import { compositeDocument } from './composite';
import type { AppStore, RgbColor } from './store';

/** 填充对话框共用的"混合"部分（内容用什么、跟混合模式/不透明度是两件独立的事）。 */
export interface FillBlendOptions {
  /** 省略/'normal' = 直接覆盖（原来的行为）。 */
  readonly blendMode?: SeparableBlendMode;
  /** 0–1，省略 = 1（完全不透明）。 */
  readonly opacity?: number;
}

/**
 * 把当前选区内、**活动 raster 图层**的像素清成透明（M3-01 起以活动图层为目标；
 * 活动图层非 raster 时回退最上层 raster）。**没有选区时清空整个活动图层**——
 * 跟 `fillSelectionWithColor` 同一个 Photoshop 行为（剪切也走这条路：没有
 * 选区时剪切整个图层，不需要先框一个选区才能用）。
 * 无文档/无 raster 图层时是无操作。返回是否有修改发生。
 */
export function clearSelectedPixels(store: AppStore): boolean {
  const document = store.document;
  if (document === null) return false;
  const rawSelection = store.selection;
  const selection = rawSelection !== null && !rawSelection.isEmpty ? rawSelection : null;

  const target: LayerId | null = store.activeRasterLayerId();
  if (target === null) return false;

  const bounds = selection !== null ? selection.contentBounds() : { x: 0, y: 0, ...document.size };
  if (bounds === null) return false;

  const transaction = store.beginPixelTransaction('删除');
  const tx0 = Math.floor(bounds.x / TILE);
  const ty0 = Math.floor(bounds.y / TILE);
  const tx1 = Math.floor((bounds.x + bounds.width - 1) / TILE);
  const ty1 = Math.floor((bounds.y + bounds.height - 1) / TILE);

  const keys = [];
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      const key = tileKey(target, tx, ty);
      transaction.touch(key);
      keys.push({ tx, ty, key });
    }
  }
  transaction.capture();

  const touched: Array<{ tx: number; ty: number }> = [];
  const row = new Uint8Array(TILE);
  for (const { tx, ty, key } of keys) {
    const existing = store.tiles.get(key)?.data;
    if (existing == null) continue; // 本来就透明，删无可删
    const out = new Uint8Array(BYTES_PER_TILE);
    out.set(existing);
    let changed = false;

    const top = Math.max(bounds.y, ty * TILE);
    const bottom = Math.min(bounds.y + bounds.height, (ty + 1) * TILE);
    const left = Math.max(bounds.x, tx * TILE);
    const right = Math.min(bounds.x + bounds.width, (tx + 1) * TILE);
    for (let y = top; y < bottom; y++) {
      if (selection !== null) selection.readRow(y, left, right, row);
      else row.fill(255, 0, right - left); // 没有选区：整块区域都当"全选"清
      for (let x = left; x < right; x++) {
        const m = row[x - left]!;
        if (m === 0) continue;
        const at = ((y % TILE) * TILE + (x % TILE)) * 4;
        const a = out[at + 3]!;
        if (a === 0) continue;
        out[at + 3] = Math.round((a * (255 - m)) / 255);
        changed = true;
      }
    }
    if (changed) {
      store.tiles.set(key, out);
      touched.push({ tx, ty });
    }
  }

  if (touched.length === 0) return false;
  store.commitPixels('删除', transaction.end());
  store.invalidatePixels(touched);
  return true;
}

/**
 * 填充纯色（编辑→填充前景色，PS 的 Alt+Delete；也是「填充…」对话框的落地
 * 通道之一）。有选区时只填选区（按比例调制，128 处效果减半）；**没有选区
 * 时填满整个活动图层**——这是 Photoshop 里 Alt+Delete 与「填充…」对话框
 * 共同的真实行为，不需要用户先框一个选区才能用（内容识别填充是例外，
 * 见 `contentAwareFillDocument`：它必须有选区才知道"洞"在哪）。
 * 一次调用 = 一条历史。返回是否有修改发生。
 */
export function fillSelectionWithColor(
  store: AppStore,
  color: RgbColor,
  blend: FillBlendOptions = {},
): boolean {
  const document = store.document;
  if (document === null) return false;
  const rawSelection = store.selection;
  const selection = rawSelection !== null && !rawSelection.isEmpty ? rawSelection : null;

  const target = store.activeRasterLayerId();
  if (target === null) return false;
  const bounds = selection !== null ? selection.contentBounds() : { x: 0, y: 0, ...document.size };
  if (bounds === null) return false;

  const transaction = store.beginPixelTransaction('填充');
  const touched = applyFill(
    store.tiles,
    target,
    bounds,
    () => [color.r, color.g, color.b, 255],
    transaction,
    {
      selection: selection ?? undefined,
      opacity: blend.opacity ?? 1,
      ...(blend.blendMode === undefined ? {} : { blendMode: blend.blendMode }),
    },
  );
  if (touched.length === 0) return false;
  store.commitPixels('填充', transaction.end());
  store.invalidatePixels(touched);
  return true;
}

/**
 * 内容识别填充（M4-11 / DESIGN.md §2.11）：选区内容替换为从选区外采样的
 * 合理填充。取整个文档的压平像素当 PatchMatch 的输入——洞（选区）需要
 * 周围看得见的上下文才能找到"够像"的源 patch，只给选区本身的矩形是
 * 没有意义的（那样每个像素都是洞，找不到任何已知内容）。
 * 一次调用 = 一条历史。返回是否有修改发生。
 */
export async function contentAwareFillDocument(
  store: AppStore,
  options: ContentAwareFillOptions & FillBlendOptions = {},
): Promise<boolean> {
  const document = store.document;
  const selection = store.selection;
  if (document === null || selection === null || selection.isEmpty) return false;

  const target = store.activeRasterLayerId();
  if (target === null) return false;
  const bounds = selection.contentBounds();
  if (bounds === null) return false;

  const { width, height } = document.size;
  const composite = compositeDocument(document, store.tiles);
  const mask = new Uint8Array(width * height);
  const row = new Uint8Array(width);
  for (let y = 0; y < height; y++) {
    selection.readRow(y, 0, width, row);
    mask.set(row, y * width);
  }

  const filled = await contentAwareFill(composite.pixels, mask, width, height, options);

  const transaction = store.beginPixelTransaction('内容识别填充');
  const touched = applyFill(
    store.tiles,
    target,
    bounds,
    (x, y) => {
      const at = (y * width + x) * 4;
      return [filled[at]!, filled[at + 1]!, filled[at + 2]!, filled[at + 3]!];
    },
    transaction,
    { selection, opacity: options.opacity ?? 1, ...(options.blendMode === undefined ? {} : { blendMode: options.blendMode }) },
  );
  if (touched.length === 0) return false;
  store.commitPixels('内容识别填充', transaction.end());
  store.invalidatePixels(touched);
  return true;
}
